/**
 * @file CountersFields.tsx
 * @description Группы панели «Счётчики и cookie» (phase19.16, партия Р3г,
 * v0.19.62; макет uiux-r3.md §3.6; SB122 — группы-карточки, память
 * `commora.settings-groups.counters`):
 *
 * ```
 * Счётчики · Когда включать · Баннер · Тексты          ← оглавление от 768
 * ┌ Счётчики ─────────────────── Метрика, VK     ⌃┐
 * ┌ Когда включать ───────── только после согласия ⌃┐
 * ┌ Баннер ───────────────────── полоса внизу     ⌃┐
 * └ Тексты ─────────────────── как у платформы   ⌃┘
 * ```
 * Без формы: группы читают общий контракт из контекста (`counters-context`).
 */

import type { SiteConsentBanner, SiteCounters, SiteTheme } from "@chaiberi/shared";
import { useMemo } from "react";

import { SettingsGroups } from "../../ui/settings-groups";
import { ConsentBannerGroup } from "./ConsentBannerGroup";
import { ConsentTextsGroup } from "./ConsentTextsGroup";
import { CountersContext, type CountersApi } from "./counters-context";
import { CountersModeGroup } from "./CountersModeGroup";
import { CountersServicesGroup } from "./CountersServicesGroup";
import type { CountersUpdateOptions } from "../../../hooks/site-view/useCountersDraft";
import type { SiteConsentPlatform } from "../../../lib/site-view/types";

interface Props {
  counters: SiteCounters;
  banner: SiteConsentBanner;
  theme: SiteTheme;
  platform: SiteConsentPlatform | undefined;
  noticeAllowed: boolean;
  onChange: (patch: Partial<SiteCounters>, options?: CountersUpdateOptions) => void;
  onBannerChange: (patch: Partial<SiteConsentBanner>) => void;
  onBannerPreview?: (patch: Partial<SiteConsentBanner>) => void;
}

const noop = (): void => undefined;

export function CountersFields({ counters, banner, theme, platform, noticeAllowed, onChange, onBannerChange, onBannerPreview }: Props) {
  const api = useMemo<CountersApi>(
    () => ({ counters, banner, theme, platform, noticeAllowed, update: onChange, updateBanner: onBannerChange, previewBanner: onBannerPreview ?? noop }),
    [counters, banner, theme, platform, noticeAllowed, onChange, onBannerChange, onBannerPreview],
  );
  return (
    <CountersContext.Provider value={api}>
      <SettingsGroups panel="counters">
        <CountersServicesGroup />
        <CountersModeGroup />
        <ConsentBannerGroup />
        <ConsentTextsGroup />
      </SettingsGroups>
    </CountersContext.Provider>
  );
}
